import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Pennant } from 'markcat-ui';
import { apiFetch, friendlyErrorMessage } from 'markcat-api-client';
import { useAuthStore } from '../stores/auth';
import { usePageTitle } from '../hooks/usePageTitle';
import { isTrustedExtensionId } from '../trustedExtensions';
import styles from './Login.module.css';

// chrome.runtime.sendMessage is injected into regular pages only when an
// installed extension lists this origin in externally_connectable — no
// @types/chrome in the web app, so declare the sliver we use.
declare const chrome: {
  runtime?: {
    sendMessage: (
      extensionId: string,
      message: unknown,
      callback: (response?: { ok?: boolean }) => void,
    ) => void;
    lastError?: { message?: string };
  };
} | undefined;

interface ExtensionTokenResponse {
  access_token: string;
  refresh_token: string;
}

type Phase = 'idle' | 'connecting' | 'done';

/**
 * Hands the signed-in session to the Markcat browser extension.
 *
 * The extension opens this page with its runtime id in ?ext=. Login happens
 * here on markcat.app — where browser password managers can save and fill
 * credentials, which they never do on chrome-extension:// pages — then one
 * click mints a dedicated token pair and messages it to the extension.
 */
export function ConnectExtension() {
  usePageTitle('Connect extension — Markcat');
  const [searchParams] = useSearchParams();
  const user = useAuthStore((s) => s.user);
  const [phase, setPhase] = useState<Phase>('idle');
  const [error, setError] = useState('');

  // The id in ?ext= decides who receives a session, so it is checked against the
  // build's allowlist, not against the id format.
  const extId = searchParams.get('ext');
  const validExtId = isTrustedExtensionId(extId) ? extId : null;
  const messagingAvailable = typeof chrome !== 'undefined' && !!chrome?.runtime?.sendMessage;

  const handleConnect = useCallback(async () => {
    if (!validExtId) return;
    setError('');
    setPhase('connecting');
    try {
      const tokens = await apiFetch<ExtensionTokenResponse>('/api/auth/extension-token', {
        method: 'POST',
      });
      const apiUrl = import.meta.env.VITE_API_URL || window.location.origin;
      chrome!.runtime!.sendMessage(
        validExtId,
        {
          type: 'mc_connect',
          apiUrl,
          accessToken: tokens.access_token,
          refreshToken: tokens.refresh_token,
        },
        (response) => {
          if (chrome?.runtime?.lastError || !response?.ok) {
            setError('Couldn’t reach the extension. Make sure it’s installed, then try again.');
            setPhase('idle');
            return;
          }
          setPhase('done');
        },
      );
    } catch (err) {
      setError(friendlyErrorMessage(err));
      setPhase('idle');
    }
  }, [validExtId]);

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <Pennant size={40} className={styles.mark} />
        <h2 className={styles.title}>Connect the extension</h2>
        {phase === 'done' ? (
          <p className={styles.subtitle} role="status">
            Extension connected as {user?.email}. You can close this tab.
          </p>
        ) : !extId ? (
          <p className={styles.error} role="alert">
            This page only works when opened from the Markcat extension.
          </p>
        ) : !validExtId ? (
          <p className={styles.error} role="alert">
            Markcat doesn’t recognise the extension that opened this page, so it won’t hand
            over your session. Reinstall the Markcat extension, then reopen this page from it.
          </p>
        ) : !messagingAvailable ? (
          <p className={styles.error} role="alert">
            Your browser can’t reach the Markcat extension. Make sure it’s installed and you’re
            using Chrome, then reopen this page from the extension.
          </p>
        ) : (
          <>
            <p className={styles.subtitle}>
              Sign the Markcat extension in as {user?.email}. The extension gets its own
              session — signing out here won’t sign it out.
            </p>
            {error && <p className={styles.error} role="alert">{error}</p>}
            <button
              className={styles.button}
              onClick={() => void handleConnect()}
              disabled={phase === 'connecting'}
              aria-busy={phase === 'connecting'}
            >
              {phase === 'connecting'
                ? <><span className="mc-spinner" aria-hidden />Connecting…</>
                : 'Connect extension'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
